/**
 * Reports Page
 * Weekly and Monthly Analytics with roll-up summaries, progress tracking, and suggestions
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import Navigation from '@/components/navigation';
import { getColorConfig } from '@/lib/notificationColorSystem';
import { useAuth } from '@/hooks/useAuth';
import {
  Calendar,
  TrendingUp,
  TrendingDown,
  Target,
  Droplets,
  Clock,
  Trophy,
  Star,
  BarChart3,
  PieChart,
  ArrowUp,
  ArrowDown,
  Minus,
  Award,
  Zap,
  Users,
} from 'lucide-react';

export default function Reports() {
  const { user } = useAuth();
  const userName = (user as any)?.firstName?.toUpperCase() || 'USER';

  const stats = {
    weeklyAverage: 2.0,
    bestDay: 'Sat',
    totalWeek: 13.8,
    goalAchieved: 6,
  };

  const badges = [
    {
      icon: Award,
      title: 'Week Warrior',
      description: '7-day streak achieved',
      color: 'bg-lilac-300',
    },
    {
      icon: Star,
      title: 'Hydration Hero',
      description: 'Hit 100% goal 5 times',
      color: 'bg-purple-300',
    },
    {
      icon: Zap,
      title: 'Quick Starter',
      description: 'Logged water before 9 AM',
      color: 'bg-lilac-200',
    },
  ];

  const communityStats = {
    yourRank: 12,
    totalMembers: 156,
    percentile: 92,
    avgOrgIntake: 1.8,
    yourAvgIntake: 2.0,
  };

  return (
    <div className="min-h-screen pb-24 bg-hydro-100">
      <div className="max-w-md mx-auto px-6 py-4 space-y-4">
        {/* Header - Centered */}
        <div className="text-center">
          <h1 className="text-xl font-bold text-deep-800 tracking-tight font-kumbh">
            HYDROAPP x {userName}
          </h1>
        </div>

        {/* Message Box */}
        <Card className="p-3 bg-lilac-100 border-lilac-300">
          <p className="text-sm text-deep-700 text-center font-kumbh">
            Hey, it's a great day to stay hydrated! 💧
          </p>
        </Card>

        {/* Individual Analytics Section */}
        <div>
          <h2 className="text-lg font-semibold text-deep-800 mb-3 font-kumbh">
            Individual Analytics
          </h2>

          {/* Stats & Chart Card */}
          <Card className="p-5 bg-gradient-to-br from-lilac-300 via-lilac-200 to-purple-200 border-none mb-4">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="flex items-center gap-2 mb-1">
                  <Droplets className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-medium text-deep-500 font-kumbh">Weekly Avg</span>
                </div>
                <div className="text-xl font-bold text-deep-800 font-kumbh">
                  {stats.weeklyAverage}L
                </div>
              </div>

              <div className="p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-medium text-deep-500 font-kumbh">Best Day</span>
                </div>
                <div className="text-xl font-bold text-deep-800 font-kumbh">{stats.bestDay}</div>
              </div>

              <div className="p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-medium text-deep-500 font-kumbh">Goals Hit</span>
                </div>
                <div className="text-xl font-bold text-deep-800 font-kumbh">
                  {stats.goalAchieved}/7
                </div>
              </div>

              <div className="p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="flex items-center gap-2 mb-1">
                  <Droplets className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-medium text-deep-500 font-kumbh">Total Week</span>
                </div>
                <div className="text-xl font-bold text-deep-800 font-kumbh">{stats.totalWeek}L</div>
              </div>
            </div>
          </Card>

          {/* Badges Card */}
          <Card className="p-5 bg-gradient-to-br from-lilac-300 via-lilac-200 to-purple-200 border-none mb-4">
            <h3 className="font-semibold text-deep-800 mb-4 flex items-center gap-2 font-kumbh">
              <Award className="w-5 h-5 text-purple-600" />
              Your Badges
            </h3>
            <div className="space-y-3">
              {badges.map((badge, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300"
                >
                  <div className={`p-2 ${badge.color} rounded-lg`}>
                    <badge.icon className="w-5 h-5 text-deep-800" />
                  </div>
                  <div>
                    <p className="font-medium text-deep-800 font-kumbh">{badge.title}</p>
                    <p className="text-xs text-deep-500 font-kumbh">{badge.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Insights Card */}
          <Card className="p-5 bg-gradient-to-br from-lilac-300 via-lilac-200 to-purple-200 border-none">
            <h3 className="font-semibold text-deep-800 mb-4 font-kumbh">Insights</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-deep-800 font-kumbh">
                    Great progress this week!
                  </p>
                  <p className="text-xs text-deep-500 font-kumbh">
                    You averaged {stats.weeklyAverage}L per day this week
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="w-2 h-2 bg-lilac-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-deep-800 font-kumbh">Stay consistent</p>
                  <p className="text-xs text-deep-500 font-kumbh">
                    Try to maintain this pace next week
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-deep-800 font-kumbh">Weekend warrior</p>
                  <p className="text-xs text-deep-500 font-kumbh">
                    Your hydration improves on weekends
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Community Comparison Section */}
        <div>
          <h2 className="text-lg font-semibold text-deep-800 mb-3 font-kumbh">Vs Community</h2>

          <Card className="p-5 bg-gradient-to-br from-lilac-300 via-lilac-200 to-purple-200 border-none">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-deep-800 font-kumbh">Organization Ranking</span>
              </div>
              <div className="flex items-center gap-1 bg-white/50 px-3 py-1 rounded-full border border-lilac-300">
                <Trophy className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-bold text-purple-600 font-kumbh">
                  #{communityStats.yourRank}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="text-center p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <p className="text-2xl font-bold text-purple-600 font-kumbh">
                  {communityStats.percentile}%
                </p>
                <p className="text-xs text-deep-500 font-kumbh">Percentile</p>
              </div>
              <div className="text-center p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <p className="text-2xl font-bold text-deep-800 font-kumbh">
                  {communityStats.totalMembers}
                </p>
                <p className="text-xs text-deep-500 font-kumbh">Team Members</p>
              </div>
            </div>

            <div className="p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-deep-600 font-kumbh">Your Avg</span>
                <span className="font-bold text-purple-600 font-kumbh">
                  {communityStats.yourAvgIntake}L
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-deep-600 font-kumbh">Org Avg</span>
                <span className="font-medium text-deep-800 font-kumbh">
                  {communityStats.avgOrgIntake}L
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Navigation />
    </div>
  );
}
